<template>
  <AppDialog :is-open="isDialogOpened"
             size="xl"
             :ok-text="$t('project.question-answer.QuestionAnswerImportDialog.import')"
             :ok-disabled="okDisabled"
             :ok-loading="importQuestionsPending"
             :cancel-disabled="importQuestionsPending"
             @ok="importFile"
             @cancel="closeDialog"
  >
    <template #title>
      {{ $t('project.question-answer.QuestionAnswerImportDialog.titleImportQuestionsAndAnswers') }}
    </template>
    <template #body>
      <!-- EXCEL FILE -->
      <ImportChecklistDialogExcelFile :templateLink="templateLink"
                                      :hasImportErrors="!!massInputError"
                                      :allowedExtensions="QA_IMPORT_ALLOWED_EXTENSIONS"
                                      isQuestionAnswerDialog
                                      @update-file="selectFile"
                                      @reset-import-errors="massInputError = null"
      >
      </ImportChecklistDialogExcelFile>

      <AppAlertInPage
        v-if="limitWarning"
        class="mt-4"
        :dismissible="false"
        variant="warning"
        data-locator="qa-import-limit-warning"
      >
        <template #title>
          {{ $t('project.question-answer.QuestionAnswerImportDialog.warning.limitExceededTitle', {
            count: limitWarning.remainingQuestions,
          }) }}
        </template>
        {{ $t('project.question-answer.QuestionAnswerImportDialog.warning.limitExceededBody', {
          count: limitWarning.remainingQuestions,
          total: limitWarning.questionsInFile,
        }) }}
      </AppAlertInPage>

      <v-row v-if="massInputError" class="mt-2">
        <v-col>
          <div class="text-red text-center">
            {{ $t('project.question-answer.QuestionAnswerImportDialog.error.massImport_' + massInputError) }}
            <template v-if="massInputError === 'ERR_INCORRECT_DATA'">
              <ul style="list-style-position: inside;" class="text-left">
                <li v-for="(error, i) in massInputErrorArray" :key="`errorLine-${i}`" class="py-1">
                  {{ $t('error.massImport_atLine')}} {{Number(i) + 2 }}
                  <ul style="list-style-position: inside;" class="ml-5">
                    <li v-for="(errorDetail, j) in error" :key="`error-${j}`">
                      {{ $t("error.massImport_" + errorDetail.errorCode) }}
                    </li>
                  </ul>
                </li>
              </ul>
            </template>
          </div>
        </v-col>
      </v-row>
    </template>
  </AppDialog>
</template>

<script>
import { mapActions, mapState } from 'vuex'

import AppAlertInPage from '@/design-system/alert/AppAlertInPage.vue'
import AppDialog from '@/common/dialogs/AppDialog.vue'
import { IMPORT_SOURCES, track } from '@/common/pendo/agent'
import { QA_IMPORT_ALLOWED_EXTENSIONS } from '@/common/utils/files'
import { toast } from '@/common/utils/toast'
import { dialogMixin } from '@/mixins/dialogMixin'
import ImportChecklistDialogExcelFile from '@/project/checklist/dialogs/ImportChecklistDialogExcelFile.vue'
import { QuestionAnswerActionTypes } from '@/store/modules/question-answer/action_types'

const MAX_FILE_SIZE = 10 * 1024 * 1024

export default {
  name: 'QuestionAnswerImportDialog',
  components: { AppAlertInPage, AppDialog, ImportChecklistDialogExcelFile },
  mixins: [dialogMixin],
  props: {
    mnemo: {
      type: String,
      required: true,
    },
  },
  data () {
    return {
      QA_IMPORT_ALLOWED_EXTENSIONS,
      selectedFile: null,
      massInputError: null,
      massInputErrorArray: [],
      limitWarning: null,
      forceImport: false,
    }
  },
  computed: {
    ...mapState('questionAnswer', ['importQuestionsPending']),
    templateLink () {
      return `${import.meta.env.VUE_APP_API_URL}/resources/massQAImportTemplate`
    },
    okDisabled () {
      return this.importQuestionsPending || !this.selectedFile || !!this.massInputError
    },
  },
  methods: {
    ...mapActions('questionAnswer', [QuestionAnswerActionTypes.IMPORT_QUESTIONS]),
    selectFile (event) {
      this.massInputError = null
      this.limitWarning = null
      this.forceImport = false
      this.selectedFile = event
      // Check for file size
      if (this.selectedFile?.size > MAX_FILE_SIZE) {
        this.massInputError = 'ERR_FILE_TOO_LARGE'
      }
    },
    async importFile () {
      try {
        const formData = new FormData()
        formData.append('file', this.selectedFile, this.selectedFile.name)
        if (this.forceImport) {
          formData.append('forceImport', '1')
        }
        await this.IMPORT_QUESTIONS({
          mnemo: this.mnemo,
          formData,
        })
        toast.success(this.$t('project.question-answer.QuestionAnswerImportDialog.importSuccess'))
        track('mass_question_created', {
          project_mnemo: this.mnemo,
          source: IMPORT_SOURCES.EXCEL,
        })
        this.closeDialog()
      } catch (error) {
        if (error.response?.data) {
          const { data: errorData, errorCode, questionsInFile, remainingQuestions } = error.response.data
          switch (errorCode) {
            case 'ERR_MAX_NUMBER_EXCEEDED': {
              if (remainingQuestions > 0) {
                this.limitWarning = { questionsInFile, remainingQuestions }
                this.forceImport = true
              } else {
                this.massInputError = errorCode
              }
              break
            }
            case 'ERR_INCORRECT_DATA':
            case 'ERR_FILE_TOO_LARGE':
            case 'ERR_FILE_WRONG_TYPE':
            case 'ERR_FILE_EMPTY':
            case 'ERR_CANNOT_BE_PROCESSED':
            case 'ERR_FILE_WRONG_FORMAT': {
              this.massInputError = errorCode
              this.massInputErrorArray = errorData
              break
            }
          }
        } else {
          toast.error(this.$t('project.question-answer.QuestionAnswerImportDialog.error.genericError'))
        }
      }
    },
  },
}
</script>
